import type { UserGroup } from "admin/types/interfaces/user";
import { Users } from "lucide-react";
import useUser from "~/contexts/user";
import { SettingsGroup } from "~/pages/manifold-nextgen/components/settings/SettingsGroup";
import { SettingsGroupState } from "~/pages/manifold-nextgen/components/settings/SettingsGroupState";
import { groupLabel, useUserGroups } from "./useUserGroups";

const GroupRow = ({ group }: { group: UserGroup }) => (
  <li className="flex items-center gap-3 px-4 py-3">
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Users className="size-4" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-foreground">{groupLabel(group)}</p>
      {group.description && (
        <p className="truncate text-xs text-muted-foreground">{group.description}</p>
      )}
    </div>
  </li>
);

/**
 * The profile's Groups card: which groups the user belongs to. Read-only —
 * membership is granted by an administrator, and it is what most of the user's
 * data and tool access is attached to.
 */
export const ProfileGroupsSection = () => {
  const user = useUser();
  const { groups, isLoading, error } = useUserGroups(user?.email);

  return (
    <SettingsGroup label="Groups" count={groups.length}>
      <SettingsGroupState
        isLoading={isLoading}
        hasError={Boolean(error)}
        isEmpty={groups.length === 0}
        errorMessage="Unable to load your groups."
        emptyMessage="You’re not in any groups."
        emptyHint="Ask an administrator to add you to one if you need access to shared data."
        skeletonRows={2}
      >
        <ul className="divide-y divide-border">
          {groups.map((group) => (
            <GroupRow key={group.name} group={group} />
          ))}
        </ul>
      </SettingsGroupState>
    </SettingsGroup>
  );
};
